import { cn } from "@/lib/utils";
import { colors, motion, spacing, type StatusColorKey } from "@/ui/tokens";
import { cva } from "class-variance-authority";

export type BaseVariant =
  | "default"
  | "secondary"
  | "destructive"
  | "outline"
  | "muted";

export type StatusVariant = StatusColorKey | "neutral";

export type BadgeVariant = BaseVariant | StatusVariant;

export const variantClasses: Record<BadgeVariant, string> = {
  default: cn(
    colors.accents.primary.background,
    colors.accents.primary.foreground,
    colors.accents.primary.border,
    colors.accents.primary.shadow,
    colors.accents.primary.hover
  ),
  secondary: cn(
    colors.accents.secondary.background,
    colors.accents.secondary.foreground,
    colors.accents.secondary.border,
    colors.accents.secondary.shadow,
    colors.accents.secondary.hover
  ),
  destructive: cn(
    colors.accents.destructive.background,
    colors.accents.destructive.foreground,
    colors.accents.destructive.border,
    colors.accents.destructive.shadow,
    colors.accents.destructive.hover
  ),
  outline: "border border-border text-foreground",
  muted: cn(
    colors.accents.muted.background,
    colors.accents.muted.foreground,
    colors.accents.muted.border,
    colors.accents.muted.shadow,
    colors.accents.muted.hover
  ),
  neutral: cn(
    colors.status.neutral.surface,
    colors.status.neutral.foreground,
    colors.status.neutral.border,
    colors.status.neutral.hover
  ),
  success: cn(
    colors.status.success.surface,
    colors.status.success.foreground,
    colors.status.success.border,
    colors.status.success.hover
  ),
  warning: cn(
    colors.status.warning.surface,
    colors.status.warning.foreground,
    colors.status.warning.border,
    colors.status.warning.hover
  ),
  danger: cn(
    colors.status.danger.surface,
    colors.status.danger.foreground,
    colors.status.danger.border,
    colors.status.danger.hover
  ),
  info: cn(
    colors.status.info.surface,
    colors.status.info.foreground,
    colors.status.info.border,
    colors.status.info.hover
  ),
  premium: cn(
    colors.status.premium.surface,
    colors.status.premium.foreground,
    colors.status.premium.border,
    colors.status.premium.hover
  ),
  zen: cn(
    colors.status.zen.surface,
    colors.status.zen.foreground,
    colors.status.zen.border,
    colors.status.zen.hover
  ),
};

export const sizeClasses = {
  sm: spacing.badge.sm,
  md: spacing.badge.md,
};

export const iconColorByVariant: Record<BadgeVariant, string> = {
  default: colors.accents.primary.icon,
  secondary: colors.accents.secondary.icon,
  destructive: colors.accents.destructive.icon,
  outline: colors.text.primary,
  muted: colors.accents.muted.icon,
  neutral: colors.status.neutral.icon,
  success: colors.status.success.icon,
  warning: colors.status.warning.icon,
  danger: colors.status.danger.icon,
  info: colors.status.info.icon,
  premium: colors.status.premium.icon,
  zen: colors.status.zen.icon,
};

export const badgeVariants = cva(
  cn(
    "inline-flex items-center font-medium whitespace-nowrap",
    spacing.radius.full,
    motion.transitions.emphasis,
    motion.focus.ring
  ),
  {
    variants: {
      variant: variantClasses,
      size: sizeClasses,
    },
    defaultVariants: {
      variant: "neutral",
      size: "md",
    },
  }
);

export type BadgePreset =
  | "approved"
  | "pending"
  | "rejected"
  | "active"
  | "inactive"
  | "unknown"
  | "processing"
  | "failed"
  | "completed"
  | "created"
  | "under_review"
  | "not_started"
  | "settled"
  | "vip"
  | "premium_tier"
  | "verified"
  | "onboarding_complete";

export const presetToVariant: Record<BadgePreset, BadgeVariant> = {
  approved: "success",
  active: "success",
  completed: "success",
  settled: "success",
  pending: "warning",
  under_review: "info",
  processing: "info",
  created: "neutral",
  not_started: "neutral",
  inactive: "neutral",
  unknown: "neutral",
  rejected: "danger",
  failed: "danger",
  // Premium/Zen presets (Canon v1.4.5)
  vip: "premium",
  premium_tier: "premium",
  verified: "zen",
  onboarding_complete: "zen",
};
